import React, { useEffect, useState } from 'react';
import Button from '../components/Button';
import { SharedKeyResponse } from '../models/api';
import {
  disableAuthentication,
  enableAuthentication,
  forgetMFAClient,
  getMFA,
  getSharedKey
} from '../services/auth.service';
import LoadingSpinner from '../components/LoadingSpinner';
import QRCode from 'qrcode.react';
import DeleteButton from '../components/DeleteButton';
import { useAuth } from '../contexts/AuthContext';
import Feedback from '../components/Feedback';

const MFA: React.FC = () => {
  const [sharedKey, setSharedKey] = useState<
    SharedKeyResponse | null | undefined
  >(null);

  const [errors, setErrors] = useState([] as string[]);
  const [message, setMessage] = useState('');
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null);
  const [isTwoFactorClientRemembered, setIsTwoFactorClientRemembered] =
    useState<boolean | null>(null);
  const [loadingState, setLoadingState] = useState(true);
  const [loadingVerification, setLoadingVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([] as string[]);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const [forgetErrors, setForgetErrors] = useState([] as string[]);
  const [forgetLoading, setForgetLoading] = useState(false);

  const auth = useAuth();

  useEffect(() => {
    const getData = async () => {
      const response = await getMFA();

      if (response.success && response.data) {
        setErrors([]);
        setIsEnabled(response.data.isEnabled);
        setIsTwoFactorClientRemembered(
          response.data.isTwoFactorClientRemembered
        );
        if (!response.data.isEnabled) {
          const keyResponse = await getSharedKey();
          if (keyResponse.success) {
            setSharedKey(keyResponse.data);
          } else {
            setErrors(keyResponse.errors);
          }
        }
      } else {
        setErrors(response.errors);
      }
      setLoadingState(false);
    };

    getData();
  }, []);

  const enable = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (verificationCode.length === 0) {
      setMessage('');
      setErrors(['Verification code is missing']);
      return;
    }

    setLoadingVerification(true);

    const result = await enableAuthentication({ code: verificationCode });
    if (result.success) {
      setErrors([]);
      setRecoveryCodes(result.data?.recoveryCodes ?? []);
      setVerificationCode('');
      setIsEnabled(true);
      setIsTwoFactorClientRemembered(false);
      setMessage(
        'MFA enabled.  You will now need to use your authenticator app when you next log in.'
      );
    } else {
      setVerificationCode('');
      setMessage('');
      setErrors(result.errors);
    }
    setLoadingVerification(false);
  };

  const disable = async () => {
    if (!isEnabled) {
      setMessage('');
      setErrors(['MFA is not enabled.']);
    }

    if (verificationCode.length === 0) {
      setMessage('');
      if (useRecoveryCode) {
        setErrors(['Recovery code is missing']);
      } else {
        setErrors(['Verification code is missing']);
      }
      return;
    }

    setLoadingVerification(true);

    const result = await disableAuthentication({
      code: verificationCode,
      useRecoveryCode
    });
    if (result.success) {
      setErrors([]);
      setRecoveryCodes([]);
      setVerificationCode('');
      setMessage(
        'MFA disabled.  You will just need to use your password when you next log in.'
      );
      setIsEnabled(false);
      setIsTwoFactorClientRemembered(false);
      setUseRecoveryCode(false);

      const keyResponse = await getSharedKey();
      if (keyResponse.success) {
        setSharedKey(keyResponse.data);
      } else {
        setErrors(keyResponse.errors);
      }
    } else {
      setVerificationCode('');
      setMessage('');
      setErrors(result.errors);
    }
    setLoadingVerification(false);
  };

  const forget = async () => {
    if (!isEnabled) {
      setMessage('');
      setForgetErrors(['MFA is not enabled.']);
    }

    setForgetLoading(true);

    const result = await forgetMFAClient();
    if (result.success) {
      setForgetErrors([]);
      setMessage('Client forgotten');
      setIsTwoFactorClientRemembered(false);
    } else {
      setMessage('');
      setForgetErrors(result.errors);
    }
    setForgetLoading(false);
  };

  if (auth.userInfo && auth.userInfo.isSSO) {
    return (
      <>
        <h2>Multi-factor authentication</h2>
        <p>
          This account in linked to a Google account, so you can&apos;t enable
          multi-factor authentication here.
        </p>
      </>
    );
  }
  return (
    <>
      <h2>Multi-factor authentication</h2>

      {!sharedKey && isEnabled === null && (
        <Feedback messages={errors} type="error" />
      )}
      {loadingState && <LoadingSpinner />}

      {!loadingState && isEnabled === true && (
        <div>
          <Feedback message={message} type="success" />
          {recoveryCodes.length > 0 && (
            <>
              <h3>Your recovery codes.</h3>
              <p>
                <strong>Put these codes in a safe place.</strong>
              </p>
              <p>
                If you lose your device and don&apos;t have the recovery codes
                you will lose access to your account.
              </p>
              <ul>
                {recoveryCodes.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </>
          )}
          <p>
            You have a configured authenticator app, so when you log in you will
            need to use this authenticator app alongside your password.
          </p>
          <form
            className="content-form"
            onSubmit={(e) => {
              e.preventDefault(); //TODO: It would be nice for a submission of the form triggers this delete button action
            }}
          >
            <h3>Remove MFA</h3>
            <div className="field">
              <label htmlFor="Input.Code" className="control-label form-label">
                Verification Code
              </label>
              <input
                id="Input.Code"
                type="text"
                autoComplete="off"
                placeholder={
                  useRecoveryCode
                    ? 'Please enter your recovery code'
                    : 'Please enter your MFA verification code'
                }
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
            </div>
            <div className="radio-container">
              <label>
                <input
                  type="radio"
                  value="useApp"
                  checked={!useRecoveryCode}
                  onChange={(e) => setUseRecoveryCode(!e.target.value)}
                />
                Use authenticator app
              </label>
              <label>
                <input
                  type="radio"
                  value="useRecoveryCode"
                  checked={useRecoveryCode}
                  onChange={(e) => setUseRecoveryCode(!!e.target.value)}
                />
                Use recovery code
              </label>
            </div>
            <div className="button-container single-button">
              <DeleteButton
                isLoading={loadingVerification}
                onClick={() => disable()}
                message="Are you sure you want to remove MFA from this account?"
              >
                Remove MFA
              </DeleteButton>
            </div>
            <Feedback messages={errors} type="error" />
          </form>
          {isTwoFactorClientRemembered && (
            <form
              className="content-form"
              onSubmit={(e) => {
                e.preventDefault(); //TODO: It would be nice for a submission of the form triggers this delete button action
              }}
            >
              <h3>Forget Client</h3>
              <p>
                This client is currently remembered so you don&apos;t need to
                authenticate using the authenticator app when you log in. You
                can forget this client so that you need to use the authenticator
                app the next time you log in.
              </p>
              <div className="button-container single-button">
                <DeleteButton
                  isLoading={forgetLoading}
                  onClick={() => forget()}
                  message="Are you sure you want to forget this client?"
                >
                  Forget Client
                </DeleteButton>
              </div>
              <Feedback messages={forgetErrors} type="error" />
            </form>
          )}
        </div>
      )}
      {!loadingState && sharedKey && isEnabled === false && (
        <div>
          <Feedback message={message} type="success" />
          <p>To use an authenticator app go through the following steps:</p>
          <ol className="list">
            <li>
              <p>
                Download a two-factor authenticator app like Microsoft
                Authenticator for{' '}
                <a
                  href="https://go.microsoft.com/fwlink/?Linkid=825072"
                  target="_blank"
                  rel="noreferrer"
                >
                  Android
                </a>{' '}
                and{' '}
                <a
                  href="https://go.microsoft.com/fwlink/?Linkid=825073"
                  target="_blank"
                  rel="noreferrer"
                >
                  iOS
                </a>{' '}
                or Google Authenticator for{' '}
                <a
                  href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&amp;hl=en"
                  target="_blank"
                  rel="noreferrer"
                >
                  Android
                </a>{' '}
                and{' '}
                <a
                  href="https://itunes.apple.com/us/app/google-authenticator/id388497605?mt=8"
                  target="_blank"
                  rel="noreferrer"
                >
                  iOS
                </a>
                .
              </p>
            </li>
            <li>
              <p>
                Scan the QR Code or enter this key{' '}
                <kbd>{sharedKey.sharedKey}</kbd> into your two factor
                authenticator app. Spaces and casing do not matter.
              </p>
              <div id="qrCode">
                <QRCode value={sharedKey.authenticatorUri} size={256} />
              </div>
            </li>
            <li>
              <p>
                Once you have scanned the QR code or input the key above, your
                two factor authentication app will provide you with a unique
                code. Enter the code in the confirmation box below.
              </p>
              <form
                id="send-code"
                className="content-form"
                onSubmit={(e) => enable(e)}
              >
                <div className="field">
                  <label
                    htmlFor="Input.Code"
                    className="control-label form-label"
                  >
                    Verification Code
                  </label>
                  <input
                    id="Input.Code"
                    type="text"
                    autoComplete="off"
                    placeholder="Please enter the code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                  />
                </div>
                <div className="button-container single-button">
                  <Button
                    isSubmit={true}
                    isLoading={loadingVerification}
                    className="action-button"
                  >
                    Verify
                  </Button>
                </div>
                <Feedback messages={errors} type="error" />
              </form>
            </li>
          </ol>
        </div>
      )}
    </>
  );
};

export default MFA;
